// src/pages/Admin/Settings/SettingsForm.jsx
import React, { useEffect, useRef, useState } from "react";
import { getSettings, updateSettings } from "../../services/settingsServices";
import {
  uploadSettingsLogoComite,
  toAbsolutePhotoUrl,
} from "../../services/settingsPhotoServices";
import Button from "../../components/Button/Button";
import { toast } from "react-toastify";
import {
  FaSave,
  FaTimes,
  FaUpload,
  FaPalette,
  FaGlobe,
  FaPhone,
  FaEnvelope,
  FaBuilding,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const SettingsForm = () => {
  const [settings, setSettings] = useState({
    nom_comite: "",
    shortname_comite: "",
    logo_comite: "",
    adresse_comite: "",
    tel_comite: "",
    mail_comite: "",
    siteweb_comite: "",
    siret_comite: "",
    facebook_comite: "",
    instagram_comite: "",
    primary_color: "#4f46e5",
    saison_active: "",
  });

  const [loading, setLoading] = useState(true);

  // Upload logo
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imgVersion, setImgVersion] = useState(0);
  const uploadDialogRef = useRef(null);

  const defaultLogo = "/img/no-image.jpg";

  const withBust = (url, v) =>
    url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          const first = settingsData[0];
          setSettings({
            ...first,
            primary_color: first?.primary_color || "#4f46e5",
          });
          setImgVersion((v) => v + 1);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedPreview("");
      return;
    }

    const blobUrl = URL.createObjectURL(selectedFile);
    setSelectedPreview(blobUrl);

    return () => URL.revokeObjectURL(blobUrl);
  }, [selectedFile]);

  useEffect(() => {
    if (!isUploadModalOpen) return;

    const onEsc = (e) => {
      if (e.key === "Escape") closeUploadModal();
    };

    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      uploadDialogRef.current
        ?.querySelector("input[type='file']")
        ?.focus?.();
    }, 0);

    return () => {
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isUploadModalOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    try {
      await updateSettings(settings);
      toast.success("Paramètres mis à jour avec succès.");
    } catch (error) {
      console.error("Erreur update settings:", error);
      toast.error("Erreur lors de la mise à jour des paramètres.");
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files?.[0] || null);
  };

  const openUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (isUploading) return;
    setIsUploadModalOpen(false);
    setSelectedFile(null);
    setSelectedPreview("");
    setUploadProgress(0);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const updated = await uploadSettingsLogoComite(selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      const newUrl = updated?.url || "";

      setSettings((prev) => ({
        ...prev,
        logo_comite: newUrl,
      }));

      setImgVersion((v) => v + 1);

      toast.success("Logo du comité mis à jour !");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur upload logo:", error?.response?.data || error);
      toast.error("Erreur lors de l'upload du logo.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getSeasonOptions = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const startYear = currentMonth >= 7 ? currentYear : currentYear - 1;

    return [
      `${startYear}-${startYear + 1}`,
      `${startYear + 1}-${startYear + 2}`,
    ];
  };

  const logoAbs = toAbsolutePhotoUrl(
    settings.logo_comite?.trim() ? settings.logo_comite : ""
  );
  const logoSrc = withBust(logoAbs || defaultLogo, imgVersion);

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-3 sm:px-6">
          <div className="rounded-3xl overflow-hidden shadow-sm border border-slate-200/70 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-72 rounded-lg bg-white/20 animate-pulse" />
              <div className="mt-2 h-4 w-96 max-w-full rounded bg-white/15 animate-pulse" />
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 rounded-3xl bg-slate-100 dark:bg-slate-800/40 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-3 sm:px-6">
        <div className="rounded-3xl overflow-hidden shadow-sm border border-slate-200/70 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50 backdrop-blur">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-white text-2xl sm:text-3xl font-extrabold tracking-tight">
                  Paramètres du Comité
                </h2>
                <p className="text-white/90 text-sm sm:text-[15px] mt-1 max-w-2xl">
                  Identité, coordonnées, réseaux sociaux, couleur principale et
                  saison active.
                </p>
              </div>

              <Button
                text="Enregistrer"
                icon={FaSave}
                onClick={handleSubmit}
                className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-2xl gap-2"
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
            {/* Identité + logo */}
            <div className="grid grid-cols-1 xl:grid-cols-[1fr,360px] gap-4 sm:gap-6">
              <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 p-4 sm:p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-2xl bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-700 dark:text-slate-200">
                    <FaBuilding />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                      Identité du comité
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Nom, nom court et logo principal.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Nom du Comité <span className="text-pink-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom_comite"
                      value={settings.nom_comite}
                      onChange={handleChange}
                      required
                      placeholder="Ex : Comité de Judo de la Gironde"
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Nom raccourci <span className="text-pink-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="shortname_comite"
                      value={settings.shortname_comite}
                      onChange={handleChange}
                      required
                      placeholder="Ex : CD33 Judo"
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Couleur primaire
                    </label>
                    <div className="mt-1 flex items-center gap-3">
                      <input
                        type="color"
                        name="primary_color"
                        value={settings.primary_color || "#4f46e5"}
                        onChange={handleChange}
                        className="h-11 w-16 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 cursor-pointer"
                        aria-label="Sélection de la couleur primaire"
                      />
                      <span
                        className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-semibold"
                        style={{
                          backgroundColor: settings.primary_color || "#4f46e5",
                          color: "#fff",
                        }}
                      >
                        {settings.primary_color || "#4f46e5"}
                      </span>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Lien du logo
                    </label>
                    <input
                      type="url"
                      name="logo_comite"
                      value={settings.logo_comite}
                      onChange={handleChange}
                      placeholder="https://... ou /uploads/..."
                      inputMode="url"
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      PNG / JPG / WEBP recommandé. Format carré conseillé.
                    </p>
                  </div>
                </div>
              </div>

              {/* Carte logo */}
              <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 p-4 sm:p-5 shadow-sm flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-2xl bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-700 dark:text-slate-200">
                    <FaUpload />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                      Logo du comité
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Aperçu et remplacement du logo.
                    </p>
                  </div>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/40 p-5">
                  <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-3xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-white shadow-sm">
                    <img
                      src={logoSrc}
                      alt="Logo du comité"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = defaultLogo;
                      }}
                    />
                  </div>

                  <Button
                    icon={FaUpload}
                    text="Changer le logo"
                    onClick={openUploadModal}
                    className="mt-5 !rounded-2xl gap-2"
                  />
                </div>
              </div>
            </div>

            {/* Coordonnées */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 p-4 sm:p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-2xl bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-700 dark:text-slate-200">
                    <FaPhone />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                      Coordonnées
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Adresse, téléphone et email.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Adresse du Comité
                    </label>
                    <input
                      type="text"
                      name="adresse_comite"
                      value={settings.adresse_comite}
                      onChange={handleChange}
                      placeholder="Rue, numéro, bâtiment..."
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="tel_comite"
                      value={settings.tel_comite}
                      onChange={handleChange}
                      inputMode="tel"
                      placeholder="Ex : 05 56 XX XX XX"
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Email
                    </label>
                    <input
                      type="email"
                      name="mail_comite"
                      value={settings.mail_comite}
                      onChange={handleChange}
                      placeholder="contact@comite.fr"
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 p-4 sm:p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-2xl bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-700 dark:text-slate-200">
                    <FaGlobe />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                      Web & administratif
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Site web, SIRET et réseaux sociaux.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Site web
                    </label>
                    <input
                      type="url"
                      name="siteweb_comite"
                      value={settings.siteweb_comite}
                      onChange={handleChange}
                      placeholder="https://..."
                      inputMode="url"
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      SIRET
                    </label>
                    <input
                      type="text"
                      name="siret_comite"
                      value={settings.siret_comite}
                      onChange={handleChange}
                      placeholder="Ex : 123 456 789 00012"
                      className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        Facebook
                      </label>
                      <input
                        type="url"
                        name="facebook_comite"
                        value={settings.facebook_comite}
                        onChange={handleChange}
                        placeholder="https://facebook.com/..."
                        inputMode="url"
                        className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        Instagram
                      </label>
                      <input
                        type="url"
                        name="instagram_comite"
                        value={settings.instagram_comite}
                        onChange={handleChange}
                        placeholder="https://instagram.com/..."
                        inputMode="url"
                        className="mt-1 w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Apparence / saison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 p-4 sm:p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-2xl bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-700 dark:text-slate-200">
                    <FaPalette />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                      Apparence
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Couleur principale utilisée par le comité.
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      name="primary_color"
                      value={settings.primary_color || "#4f46e5"}
                      onChange={handleChange}
                      className="h-12 w-16 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 cursor-pointer"
                    />
                    <div
                      className="rounded-2xl px-4 py-3 text-sm font-bold shadow-sm"
                      style={{
                        backgroundColor: settings.primary_color || "#4f46e5",
                        color: "#fff",
                      }}
                    >
                      Aperçu couleur
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 p-4 sm:p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-2xl bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-700 dark:text-slate-200">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                      Saison active
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Saison de référence du comité.
                    </p>
                  </div>
                </div>

                <select
                  name="saison_active"
                  value={settings.saison_active}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {getSeasonOptions().map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* actions bas */}
            <div className="flex justify-end">
              <Button
                text="Mettre à jour"
                type="submit"
                icon={FaSave}
                className="!rounded-2xl gap-2"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Modal upload */}
      {isUploadModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeUploadModal();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Changer le logo du comité"
        >
          <div
            ref={uploadDialogRef}
            className="relative w-full max-w-lg overflow-hidden rounded-3xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95"
          >
            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
              <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                Changer le logo du comité
              </h3>
              <button
                type="button"
                onClick={closeUploadModal}
                className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
                title="Fermer"
                disabled={isUploading}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="p-4 sm:p-6 space-y-4">
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4 flex items-center gap-4">
                <img
                  src={logoSrc}
                  alt="Logo actuel"
                  className="h-16 w-16 rounded-2xl object-contain bg-white ring-1 ring-slate-200 dark:ring-slate-700"
                  onError={(e) => {
                    e.currentTarget.src = defaultLogo;
                  }}
                />
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  <div className="font-semibold text-slate-900 dark:text-white">
                    Logo actuel
                  </div>
                  <div className="text-xs mt-1">
                    Formats acceptés : PNG, JPG, WEBP
                  </div>
                </div>
              </div>

              <input
                type="file"
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/webp"
                className="w-full"
                disabled={isUploading}
              />

              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Format
                </span>
                <select
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-sm"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  disabled={isUploading}
                >
                  <option value="webp">WEBP (recommandé)</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                </select>
              </div>

              {selectedPreview && (
                <div className="rounded-2xl border border-violet-200 dark:border-violet-900/40 bg-violet-50 dark:bg-violet-950/20 p-4 flex items-center gap-4">
                  <img
                    src={selectedPreview}
                    alt="Aperçu"
                    className="h-16 w-16 rounded-2xl object-contain bg-white ring-1 ring-violet-300 dark:ring-violet-800"
                  />
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    <div className="font-semibold text-slate-900 dark:text-white">
                      Aperçu du nouveau logo
                    </div>
                    <div className="mt-1">
                      {selectedFile?.name} —{" "}
                      {selectedFile ? (selectedFile.size / 1024).toFixed(0) : 0} Ko
                    </div>
                  </div>
                </div>
              )}

              {isUploading && (
                <div>
                  <progress
                    className="progress w-full"
                    value={uploadProgress}
                    max="100"
                  />
                  <div className="text-xs mt-1 text-slate-600 dark:text-slate-300">
                    {uploadProgress}%
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  disabled={isUploading}
                  className="rounded-2xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <FaTimes /> Fermer
                </button>

                <Button
                  text={isUploading ? "Transfert..." : "Sauvegarder"}
                  type="submit"
                  icon={FaSave}
                  className="!rounded-2xl gap-2"
                  disabled={isUploading || !selectedFile}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default SettingsForm;